import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import authService from '../services/auth.service';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  user: any;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: authService.isAuthenticated(),
  login: async () => false,
  logout: () => {},
  user: null,
  loading: true
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(authService.isAuthenticated());
  const [user, setUser] = useState<any>(authService.getCurrentUser());
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Verify token and authentication status on app load
    const verifyAuth = async () => {
      setLoading(true);
      try {
        if (authService.isAuthenticated()) {
          const isValid = await authService.verifyToken();
          setIsAuthenticated(isValid);
          if (!isValid) {
            // Clear invalid auth data
            authService.logout();
            setUser(null);
          } else {
            setUser(authService.getCurrentUser());
          }
        }
      } catch (error) {
        console.error('Auth verification error:', error);
        setIsAuthenticated(false);
        authService.logout();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const success = await authService.login({ email, password });
      if (success) {
        setIsAuthenticated(true);
        setUser(authService.getCurrentUser());
      }
      return success;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;